import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import {
  CheckCircle,
  ExternalLink,
  Hourglass,
  OctagonAlert,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import {
  deleteCustomDomainHost,
  refreshCustomDomainHost,
} from 'app/frontend/base/api/team'
import {
  deleteGuildCustomDomainHost,
  refreshGuildCustomDomainHost,
} from 'app/frontend/base/api/guild'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import CustomDomainHostStatus from 'app/frontend/types/custom/CustomDomainHostStatuses'
import { cn } from '@/lib/utils'

export type EditableCustomDomainHost = Partial<
  wowaudit.CustomDomainHost & {
    subdomain?: string
    _destroy?: boolean
  }
>

export type CustomDomainOwner = Pick<
  wowaudit.Team | wowaudit.Guild,
  'id' | 'path' | 'customDomainAttributes'
>

interface HostCardProps {
  host: wowaudit.CustomDomainHost
  entityType: 'team' | 'guild'
  entity: CustomDomainOwner
}

const hostStatusColors: Record<
  CustomDomainHostStatus,
  { border: string; bg: string; icon?: React.ReactNode }
> = {
  active: {
    border: 'border-success-foreground',
    bg: 'bg-success',
    icon: <CheckCircle size={14} className='text-success-foreground' />,
  },
  available: { border: 'border-success-foreground', bg: 'bg-success' },
  taken: { border: 'border-destructive-foreground', bg: 'bg-destructive' },
  blacklisted: {
    border: 'border-destructive-foreground',
    bg: 'bg-destructive',
  },
  invalid_format: {
    border: 'border-destructive-foreground',
    bg: 'bg-destructive',
  },
  dns_pending: {
    border: 'border-warning-foreground',
    bg: 'bg-warning',
    icon: <OctagonAlert size={14} className='text-warning-foreground' />,
  },
  cert_pending: {
    border: 'border-info-foreground',
    bg: 'bg-info',
    icon: <Hourglass size={14} className='text-info-foreground' />,
  },
} as const

const HostCard = ({ host, entityType, entity }: HostCardProps) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const normalizeHost = (value: string) =>
    value.toLowerCase().replace(/\.$/, '')
  const currentHost =
    typeof window === 'undefined' ? '' : window.location.hostname
  const isCurrentHost = normalizeHost(host.host) === normalizeHost(currentHost)

  const { mutate: deleteHost, isPending } = useMutation<
    wowaudit.Team | wowaudit.Guild
  >({
    mutationFn: () =>
      entityType === 'team'
        ? deleteCustomDomainHost(entity.id, host.id)
        : deleteGuildCustomDomainHost(entity.id, host.id),
    onSuccess: (newData) => {
      toast.success('Host deleted')
      queryClient.setQueryData([entityType, { path: entity.path }], newData)
    },
    onError: (error) => displayErrorToast(error, t),
  })

  const { mutate: refreshHost, isPending: isRefreshing } = useMutation<
    wowaudit.Team | wowaudit.Guild
  >({
    mutationFn: () =>
      entityType === 'team'
        ? refreshCustomDomainHost(entity.id, host.id)
        : refreshGuildCustomDomainHost(entity.id, host.id),
    onSuccess: (newData) => {
      toast.success('Domain status refreshed')
      queryClient.setQueryData([entityType, { path: entity.path }], newData)
    },
    onError: (error) => displayErrorToast(error, t),
  })

  return (
    <>
      <Card className='py-2 px-3'>
        <div
          className={cn(
            'flex items-center gap-2 justify-between',
            host.status === 'dns_pending' || host.status === 'cert_pending'
              ? 'border-b mb-2 pb-2'
              : '',
          )}
        >
          <a
            href={`https://${host.host}`}
            target='_blank'
            rel='noopener noreferrer'
            className='flex items-center gap-2 text-base text-primary hover:text-primary/90'
          >
            <ExternalLink size={14} />
            <span>{host.host}</span>
          </a>

          <div className='flex items-center gap-2'>
            <div
              className={cn(
                'border h-8 flex items-center justify-center px-3 rounded-md gap-2',
                hostStatusColors[host.status as CustomDomainHostStatus].border,
                hostStatusColors[host.status as CustomDomainHostStatus].bg,
              )}
            >
              {hostStatusColors[host.status as CustomDomainHostStatus].icon}
              {t(`errors.custom_domain.host_statuses.${host.status}`)}
            </div>

            <Button
              type='button'
              size='icon'
              variant='outline'
              className='shrink-0 h-8 w-8'
              loading={isRefreshing}
              onClick={() => refreshHost()}
            >
              <RefreshCw size={14} />
            </Button>

            {isCurrentHost ? (
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      type='button'
                      size='icon'
                      variant='destructive'
                      loading={isPending}
                      className='shrink-0 h-8 w-8'
                      disabled
                    >
                      <Trash2 size={14} />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  You cannot delete the domain you are currently using.
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                type='button'
                size='icon'
                variant='destructive'
                loading={isPending}
                className='shrink-0 h-8 w-8'
                onClick={() => setConfirmationOpen(true)}
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </div>

        {host.status === 'dns_pending' ? (
          <Alert variant='warning'>
            <AlertTitle>
              {t('errors.custom_domain.dns_instructions_title')}
            </AlertTitle>
            <AlertDescription className='space-y-1'>
              <p className='border-b pb-2 mb-2 border-warning-foreground/30'>
                {t('errors.custom_domain.dns_instructions_description')}
              </p>
              <p>
                Type:{' '}
                <span className='font-mono border rounded-md bg-card px-1 py-0.5'>
                  TXT
                </span>
              </p>
              <p>
                Name:{' '}
                <span className='font-mono border rounded-md bg-card px-1 py-0.5'>
                  _wowaudit-verification.{host.host}
                </span>
              </p>
              <p className='border-b pb-2 mb-2 border-warning-foreground/30'>
                Value:{' '}
                <span className='font-mono border rounded-md bg-card px-1 py-0.5'>
                  {host.verificationKey}
                </span>
              </p>
              <p>
                Type:{' '}
                <span className='font-mono border rounded-md bg-card px-1 py-0.5'>
                  A
                </span>
              </p>
              <p>
                Name:{' '}
                <span className='font-mono border rounded-md bg-card px-1 py-0.5'>
                  {host.host}
                </span>
              </p>
              <p>
                Value:{' '}
                <span className='font-mono border rounded-md bg-card px-1 py-0.5'>
                  138.197.48.16
                </span>
              </p>
            </AlertDescription>
          </Alert>
        ) : null}

        {host.status === 'cert_pending' ? (
          <Alert variant='info'>
            <AlertTitle>Certificate pending</AlertTitle>
            <AlertDescription>
              The domain&apos;s SSL certificate is being provisioned, this can
              take a few minutes.
            </AlertDescription>
          </Alert>
        ) : null}
      </Card>

      <ConfirmDialog
        open={confirmationOpen}
        descriptionKey='settings.team.custom_domain.delete-host-confirmation'
        variant='destructive'
        onCancel={() => setConfirmationOpen(false)}
        onContinue={() => {
          setConfirmationOpen(false)
          deleteHost()
        }}
      />
    </>
  )
}

const CustomDomainHosts = ({
  entityType,
  entity,
}: {
  entityType: 'team' | 'guild'
  entity: CustomDomainOwner
}) => {
  const activeHosts = entity.customDomainAttributes?.hostsAttributes ?? []

  return (
    <div>
      <div className='flex flex-col gap-2'>
        {activeHosts.length === 0 && (
          <Alert>
            <AlertTitle>No hosts configured.</AlertTitle>
            <AlertDescription>
              Add at least one host to get started with your custom domain.
            </AlertDescription>
          </Alert>
        )}

        {activeHosts.map((host, index) => (
          <HostCard
            key={`${host.id}-${index}`}
            host={host}
            entityType={entityType}
            entity={entity}
          />
        ))}
      </div>
    </div>
  )
}

export default CustomDomainHosts
